import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import usePublicMode from "../../hooks/usePublicMode";
import { useAppStore } from "../../stores/appStore";

type LogoSize = "x-small" | "small" | "medium" | "large" | "x-large" | "xxx-large";

const SIZE_TO_WIDTH_PX: Record<LogoSize, number> = {
  "x-small": 32,
  small: 48,
  medium: 70,
  large: 100,
  "x-large": 140,
  "xxx-large": 240,
};

const LOGO_ASPECT_RATIO = 463 / 74;
const STACKED_LOGO_ASPECT_RATIO = 245 / 146;

interface SudowriteLogoProps extends Omit<React.HTMLAttributes<HTMLElement>, "size"> {
  size?: LogoSize;
  stacked?: boolean;
  opacity?: number;
  isLink?: boolean;
}

const Logo = styled.span<{
  $widthPx: number;
  $stacked: boolean;
  $assetPrefix: string;
  $opacity?: number;
}>`
  display: inline-block;
  width: ${({ $widthPx }) => `${$widthPx}px`};
  aspect-ratio: ${({ $stacked }) => ($stacked ? STACKED_LOGO_ASPECT_RATIO : LOGO_ASPECT_RATIO)};
  color: var(--grey-0, #383838);
  background-color: currentColor;
  ${({ $opacity }) => ($opacity != null ? `opacity: ${$opacity};` : "")}
  -webkit-mask-image: url(${({ $assetPrefix, $stacked }) =>
    `${$assetPrefix}/${$stacked ? "sudowrite-logo-stacked.svg" : "sudowrite-logo.svg"}`});
  mask-image: url(${({ $assetPrefix, $stacked }) =>
    `${$assetPrefix}/${$stacked ? "sudowrite-logo-stacked.svg" : "sudowrite-logo.svg"}`});
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
`;

function SudowriteLogo({
  size = "medium",
  stacked = false,
  opacity,
  isLink = false,
  ...props
}: SudowriteLogoProps) {
  const { isPublicPluginsMode } = usePublicMode();
  const assetPrefix = isPublicPluginsMode ? "/plugins/assets" : "/assets";
  const user = useAppStore((state) => state.user);
  const isLoggedIn = Boolean(user && !user.isAnonymous);

  const logo = (
    <Logo
      aria-label="Sudowrite"
      role="img"
      $assetPrefix={assetPrefix}
      $widthPx={SIZE_TO_WIDTH_PX[size]}
      $stacked={stacked}
      $opacity={opacity}
      {...(isLink ? {} : props)}
    />
  );

  if (!isLink) return logo;

  return isLoggedIn ? (
    <Link
      to="/app"
      aria-label="Sudowrite home"
      {...props}
    >
      {logo}
    </Link>
  ) : (
    <a
      href={process.env.REACT_APP_MARKETING_PAGE_URL}
      aria-label="Sudowrite home"
      {...props}
    >
      {logo}
    </a>
  );
}

export default SudowriteLogo;
